import { throttle } from "lodash";

interface GenerateHtmlContentOptions {
  body: string;
  head: string;
}

export const generateHtmlContent = ({
  head,
  body,
}: GenerateHtmlContentOptions) => `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    ${head || ""}
  </head>
  <body style="margin: 0; padding: 0; overflow: hidden;">
    ${body || "<div></div>"}
  </body>
</html>`;

interface SetupResizeObserverOptions {
  iframe: HTMLIFrameElement;
  iframeDoc: Document;
}

export const setupResizeObserver = ({
  iframe,
  iframeDoc,
}: SetupResizeObserverOptions) => {
  const MIN_HEIGHT = 320;
  const updateHeight = throttle(() => {
    if (!iframeDoc.body) return;
    const newHeight = iframeDoc.body.scrollHeight;
    iframe.style.height = `${Math.max(newHeight, MIN_HEIGHT)}px`;
  }, 100);

  updateHeight();

  const mutationObserver = new MutationObserver(updateHeight);

  if (iframeDoc.body) {
    mutationObserver.observe(iframeDoc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  }

  const resizeObserver = new ResizeObserver(updateHeight);
  if (iframeDoc.body) {
    resizeObserver.observe(iframeDoc.body);
  }

  if (iframe.contentWindow) {
    iframe.contentWindow.addEventListener("resize", updateHeight);
  }

  return {
    disconnect: () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (iframe.contentWindow) {
        iframe.contentWindow.removeEventListener("resize", updateHeight);
      }
    },
  };
};

interface GetDimensionStyleOptions {
  height: string | number;
  width: string | number;
}

export const getDimensionStyle = ({
  height,
  width,
}: GetDimensionStyleOptions) => {
  const MIN_HEIGHT = "320px";
  const styleObj: Record<string, string> = {
    width: typeof width === "number" ? `${width}px` : width,
    border: "none",
  };

  if (height !== "auto") {
    styleObj.height = typeof height === "number" ? `${height}px` : height;
  }

  styleObj.minHeight = MIN_HEIGHT;

  return styleObj;
};
